import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import OutsideClickHandler from 'src/components/OutsideClickHandler';

export interface IFilterOption {
    value: string;
    label: string;
}

interface IProps<T extends string = string> {
    options: IFilterOption[];
    selectedValue: T | null;
    onSelect: (value: T) => void;
    placeholder?: string;
    hideOnSingleOption?: boolean;
    labelClassName?: string;
    dropdownPosition?: 'left' | 'right';
    isLoading?: boolean;
    isDefaultLabelColor?: boolean;
    parentClassNameOfDropdown?: string;
    listItemClassName?: string;
    containerClassName?: string;
    triggerClassName?: string;
}

const FilterDropdown = <T extends string = string>({
    options,
    selectedValue,
    onSelect,
    placeholder = 'Select',
    hideOnSingleOption = true,
    labelClassName = 'text-xs leading-[18px] font-normal',
    dropdownPosition = 'right',
    isLoading = false,
    isDefaultLabelColor = true,
    parentClassNameOfDropdown = '',
    listItemClassName = '',
    containerClassName = 'relative inline-block',
    triggerClassName = '',
}: IProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label || placeholder;

    const handleSelect = (value: T) => {
        onSelect(value);
        setIsOpen(false);
    };

    if (hideOnSingleOption && options.length <= 1) return null;

    return (
        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)} containerClassName={containerClassName}>
            <div className={`flex items-center gap-1.5 cursor-pointer ${triggerClassName} ${isLoading ? 'custom-loading rounded' : ''}`} onClick={() => !isLoading && setIsOpen(!isOpen)}>
                <p className={`${isDefaultLabelColor ? 'text-grayLight900' : ''} ${labelClassName}`}>{isLoading ? 'Loading...' : selectedLabel}</p>
                <ChevronDown className={`w-4 h-4 text-gray500 transition-transform flex-none ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            {isOpen && (
                <div className={`absolute ${dropdownPosition === 'left' ? 'left-0' : 'right-0'} top-full mt-1.5 bg-white border border-utilityGray200 rounded-xl shadow-lg z-10 min-w-[200px]`}>
                    <ul className={`p-2 ${parentClassNameOfDropdown}`}>
                        {options.map((option) => (
                            <li
                                key={option.value}
                                className={`px-2.5 py-2 text-xs leading-5 text-grayLight900 hover:bg-gray50 rounded-md cursor-pointer ${
                                    selectedValue === option.value ? 'bg-gray50 font-medium' : 'font-normal'
                                } ${listItemClassName}`}
                                onClick={() => handleSelect(option.value as T)}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </OutsideClickHandler>
    );
};

export default FilterDropdown;
